/**
 * Dunyo soatlari hisob-kitobi (test: time.test.ts). Vaqt -- Intl (brauzerning zona bazasi), yozgi vaqt o'tishlari ham.
 * Formatlovchilar keshda: Intl.DateTimeFormat yaratish qimmat, soatlar esa har daqiqada qayta chiziladi.
 */
import { STATE_TZ } from "@/features/drivers/timezones";

export interface ZoneParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(tz, f);
  }
  return f;
}

export function isValidZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || !tz) return false;
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

export function zoneParts(date: Date, tz: string): ZoneParts {
  const out: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(date)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return { year: out.year, month: out.month, day: out.day, hour: out.hour % 24, minute: out.minute, second: out.second };
}

/** Zonaning UTC'dan farqi, daqiqada (Toshkent +300, New York qishda -300, yozda -240) */
export function utcOffset(date: Date, tz: string): number {
  const p = zoneParts(date, tz);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((wall - Math.floor(date.getTime() / 1000) * 1000) / 60_000);
}

/** Zona uy vaqtidan qancha farq qiladi, daqiqada */
export function relativeOffset(date: Date, tz: string, homeTz: string): number {
  return utcOffset(date, tz) - utcOffset(date, homeTz);
}

/** Zonadagi sana uy sanasiga nisbatan: -1 kecha, 0 bugun, 1 ertaga */
export function dayDelta(date: Date, tz: string, homeTz: string): number {
  const a = zoneParts(date, tz);
  const b = zoneParts(date, homeTz);
  return Math.round((Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day)) / 86_400_000);
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "+9", "−9", "+5:30", "0" (haqiqiy minus belgisi) */
export function formatOffset(minutes: number): string {
  if (minutes === 0) return "0";
  const abs = Math.abs(minutes);
  const m = abs % 60;
  return `${minutes < 0 ? "−" : "+"}${Math.floor(abs / 60)}${m ? `:${pad(m)}` : ""}`;
}

/** Soat yuzi: 6:00--18:00 kunduz (och yuz), qolgani tun (to'q yuz) -- Apple kabi */
export const isDaytime = (hour: number) => hour >= 6 && hour < 18;

/** Qo'ng'iroq vaqti ichidami: [boshlanish, tugash) zona mahalliy soatida */
export function inCallHours(p: Pick<ZoneParts, "hour" | "minute">, call: readonly [number, number]): boolean {
  const h = p.hour + p.minute / 60;
  return h >= call[0] && h < call[1];
}

export function clockText(p: Pick<ZoneParts, "hour" | "minute">, hour12: boolean): { time: string; suffix: string } {
  if (!hour12) return { time: `${pad(p.hour)}:${pad(p.minute)}`, suffix: "" };
  return { time: `${p.hour % 12 || 12}:${pad(p.minute)}`, suffix: p.hour < 12 ? "AM" : "PM" };
}

/** Sozlamada "Avto": inglizchada 12 soatlik (AQSh), o'zbek va ruschada 24 soatlik */
export const autoHour12 = (lang: string) => lang === "en";

// --- Qo'ng'iroq lentasi (L o'lcham) --------------------------------------------------------------------------------

export type Band = "call" | "day" | "night";
export interface Segment {
  /** Lenta enining ulushi, 0..1 */
  from: number;
  to: number;
  kind: Band;
}

/** Lenta oynasi: hozirdan 6 soat oldin -- 18 soat keyin (hozirgi vaqt chiziq 1/4 da) */
export const TIMELINE_BEFORE_H = 6;
export const TIMELINE_HOURS = 24;
const STEP_MIN = 15;

export function timeline(now: Date, tz: string, call: readonly [number, number]): Segment[] {
  const steps = (TIMELINE_HOURS * 60) / STEP_MIN;
  const start = now.getTime() - TIMELINE_BEFORE_H * 3_600_000;
  const out: Segment[] = [];
  for (let i = 0; i < steps; i++) {
    // Bo'lak o'rtasi: soat chegarasida turgan "hozir" qaysi tomonga tushishi aniq bo'lsin
    const p = zoneParts(new Date(start + (i + 0.5) * STEP_MIN * 60_000), tz);
    const kind: Band = inCallHours(p, call) ? "call" : isDaytime(p.hour) ? "day" : "night";
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.to = (i + 1) / steps;
    else out.push({ from: i / steps, to: (i + 1) / steps, kind });
  }
  return out;
}

// --- Shahar nomlari va zona qidiruvi -------------------------------------------------------------------------------

/** Yuqori panel uchun qisqa kod; ro'yxatda yo'q bo'lsa shahar nomining 3 harfi */
const SHORT: Record<string, string> = {
  "America/New_York": "NY",
  "America/Chicago": "CHI",
  "America/Denver": "DEN",
  "America/Phoenix": "PHX",
  "America/Los_Angeles": "LA",
  "America/Anchorage": "ANC",
  "Pacific/Honolulu": "HNL",
  "Asia/Tashkent": "TAS",
  "Asia/Samarkand": "SKD",
  "Europe/Moscow": "MSK",
  "Europe/London": "LON",
  "Europe/Istanbul": "IST",
  "Asia/Dubai": "DXB",
  UTC: "UTC",
};

/** Zona nomidan shahar: "America/New_York" -> "New York" (tarjimasi bo'lmasa) */
export function zoneCity(tz: string): string {
  return (tz.split("/").pop() ?? tz).replace(/_/g, " ");
}

export function zoneShort(tz: string): string {
  return SHORT[tz] ?? zoneCity(tz).slice(0, 3).toUpperCase();
}

/** AQSh zonasi qaysi shtatlarni qamraydi (qidiruvda "TX" yozsa ham topilsin) */
export function zoneStates(tz: string): string[] {
  return Object.entries(STATE_TZ)
    .filter(([, z]) => z === tz)
    .map(([s]) => s)
    .sort();
}

export const US_ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];
/** Bo'sh qidiruvda taklif qilinadiganlar */
export const POPULAR_ZONES = [...US_ZONES, "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "Asia/Tashkent", "UTC"];

let zoneList: string[] | null = null;
export function allZones(): string[] {
  if (!zoneList) {
    let list: string[] = [];
    try {
      list = Intl.supportedValuesOf("timeZone");
    } catch {
      list = [...POPULAR_ZONES];
    }
    zoneList = [...new Set([...list, ...POPULAR_ZONES])];
  }
  return zoneList;
}

/** Qidiruv: shahar nomi (ko'rinib turgan tilda), zona nomi yoki AQSh shtati kodi bo'yicha; boshidan mos kelgani oldin */
export function searchZones(query: string, cityOf: (tz: string) => string, zones = allZones(), limit = 8): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return POPULAR_ZONES.slice(0, limit);
  const state = q.length === 2 ? STATE_TZ[q.toUpperCase()] : undefined;
  const scored: [number, string][] = [];
  for (const tz of zones) {
    const city = cityOf(tz).toLowerCase();
    const id = tz.toLowerCase().replace(/_/g, " ");
    if (tz === state) scored.push([0, tz]);
    else if (city.startsWith(q)) scored.push([1, tz]);
    else if (city.includes(q) || id.includes(q)) scored.push([2, tz]);
  }
  scored.sort((a, b) => a[0] - b[0] || cityOf(a[1]).localeCompare(cityOf(b[1])));
  return scored.slice(0, limit).map(([, tz]) => tz);
}
